/*
 * The strategy form, shared by "new" and "edit".
 *
 * Two things it does that a plain form would not:
 *
 * The indicator's own settings change with the rule chosen, and are described
 * in `PARAM_FIELDS` rather than written out per kind — so adding a rule to the
 * back end needs one entry there and nothing here.
 *
 * The limits are checked in the browser as well as on the server, and both say
 * the same sentence. The server's check is the one that counts; this one is
 * so a mistyped number is caught before a round trip rather than after.
 */

import type { Interval, Product, StrategyKind } from "../api/types";
import {
  DEFAULT_PARAMS,
  INTERVALS,
  KIND_BLURB,
  KIND_LABEL,
  KIND_RISK,
  PARAM_FIELDS,
  roundTripCost,
  rupees,
} from "../lib/format";

export interface FormState {
  name: string;
  kind: StrategyKind;
  params: Record<string, number>;
  interval: Interval;
  product: Product;
  perTradeRupees: number;
  maxPositions: number;
  maxTradesPerDay: number;
  dailyLossRupees: number;
  stopLossPct: string;
  targetPct: string;
  trailingPct: string;
  entryFrom: string;
  entryTo: string;
  squareOffAt: string;
  webhookSecret: string;
}

/**
 * The same conservative defaults the server would apply if none were sent.
 *
 * Daily candles, not five-minute ones. That is the largest single lever on
 * risk here and it is set this way on purpose: a five-minute rule decides
 * seventy-five times a day and pays costs on every decision it acts on, and on
 * a small account those costs are larger than the edge most rules have. A
 * daily rule decides once.
 *
 * The stop is wider than it looks it should be for the same reason — 2% is
 * inside a normal day's range on most NSE stocks, so a 2% stop on a daily
 * chart is not caution, it is a way of being taken out by noise and paying for
 * the privilege.
 */
export function blankForm(): FormState {
  return {
    name: "",
    kind: "supertrend",
    params: { ...DEFAULT_PARAMS.supertrend },
    interval: "day",
    product: "CNC",
    perTradeRupees: 5_000,
    maxPositions: 2,
    maxTradesPerDay: 3,
    dailyLossRupees: 500,
    stopLossPct: "5",
    targetPct: "10",
    trailingPct: "",
    entryFrom: "09:30",
    entryTo: "14:45",
    squareOffAt: "",
    webhookSecret: "",
  };
}

export function formFrom(s: {
  name: string;
  kind: StrategyKind;
  params: Record<string, number>;
  interval: Interval;
  product: Product;
  perTradeRupees: number;
  maxPositions: number;
  maxTradesPerDay: number;
  dailyLossRupees: number;
  stopLossPct: number | null;
  targetPct: number | null;
  trailingPct: number | null;
  entryFrom: string;
  entryTo: string;
  squareOffAt: string | null;
}): FormState {
  return {
    name: s.name,
    kind: s.kind,
    params: { ...s.params },
    interval: s.interval,
    product: s.product,
    perTradeRupees: s.perTradeRupees,
    maxPositions: s.maxPositions,
    maxTradesPerDay: s.maxTradesPerDay,
    dailyLossRupees: s.dailyLossRupees,
    stopLossPct: s.stopLossPct === null ? "" : String(s.stopLossPct),
    targetPct: s.targetPct === null ? "" : String(s.targetPct),
    trailingPct: s.trailingPct === null ? "" : String(s.trailingPct),
    entryFrom: s.entryFrom,
    entryTo: s.entryTo,
    squareOffAt: s.squareOffAt ?? "",
    webhookSecret: "",
  };
}

/** An empty percentage means "no stop", which is null and not zero. */
function orNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function toPayload(f: FormState): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: f.name,
    kind: f.kind,
    params: f.params,
    interval: f.interval,
    product: f.product,
    perTradeRupees: Number(f.perTradeRupees),
    maxPositions: Number(f.maxPositions),
    maxTradesPerDay: Number(f.maxTradesPerDay),
    dailyLossRupees: Number(f.dailyLossRupees),
    stopLossPct: orNull(f.stopLossPct),
    targetPct: orNull(f.targetPct),
    trailingPct: orNull(f.trailingPct),
    entryFrom: f.entryFrom,
    entryTo: f.entryTo,
    squareOffAt: f.squareOffAt || null,
  };
  if (f.kind === "webhook" && f.webhookSecret) body.webhookSecret = f.webhookSecret;
  return body;
}

interface Props {
  value: FormState;
  onChange: (next: FormState) => void;
  /** The rule cannot be changed after creation — its parameters can. */
  lockKind?: boolean;
}

export function StrategyForm({ value, onChange, lockKind }: Props) {
  const set = <K extends keyof FormState>(key: K, v: FormState[K]) =>
    onChange({ ...value, [key]: v });

  const fields = PARAM_FIELDS[value.kind];

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="grid cols-2">
        <div className="field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            value={value.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Morning EMA on large caps"
            maxLength={80}
          />
        </div>
        <div className="field">
          <label htmlFor="kind">Rule</label>
          <select
            id="kind"
            value={value.kind}
            disabled={lockKind}
            onChange={(e) => {
              const kind = e.target.value as StrategyKind;
              // Yesterday's parameters mean nothing to a different indicator,
              // so they are replaced rather than carried over.
              onChange({ ...value, kind, params: { ...DEFAULT_PARAMS[kind] } });
            }}
          >
            {(Object.keys(KIND_LABEL) as StrategyKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]} — {KIND_RISK[k].label}
              </option>
            ))}
          </select>
          <span className="hint">{KIND_BLURB[value.kind]}</span>
          <span className={`hint ${KIND_RISK[value.kind].tone}`}>
            {KIND_RISK[value.kind].note}
          </span>
        </div>
      </div>

      {fields.length > 0 && (
        <div className="grid cols-3">
          {fields.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={f.key}>{f.label}</label>
              <input
                id={f.key}
                type="number"
                min={f.min}
                max={f.max}
                step={f.step ?? 1}
                value={value.params[f.key] ?? ""}
                onChange={(e) =>
                  set("params", { ...value.params, [f.key]: Number(e.target.value) })
                }
              />
              {f.hint && <span className="hint">{f.hint}</span>}
            </div>
          ))}
        </div>
      )}

      {value.kind === "webhook" && (
        <div className="field">
          <label htmlFor="secret">Webhook secret</label>
          <input
            id="secret"
            type="password"
            value={value.webhookSecret}
            onChange={(e) => set("webhookSecret", e.target.value)}
            placeholder="At least 16 characters"
            autoComplete="off"
          />
          <span className="hint">
            Whatever posts signals here must present this. It is stored as a digest
            and never shown again, so keep a copy.
          </span>
        </div>
      )}

      <div className="grid cols-3">
        <div className="field">
          <label htmlFor="interval">Candle</label>
          <select
            id="interval"
            value={value.interval}
            onChange={(e) => set("interval", e.target.value as Interval)}
          >
            {INTERVALS.map((i) => (
              <option key={i.value} value={i.value}>{i.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="product">Product</label>
          <select
            id="product"
            value={value.product}
            onChange={(e) => set("product", e.target.value as Product)}
          >
            <option value="CNC">CNC — delivery</option>
            <option value="MIS">MIS — intraday</option>
          </select>
          <span className="hint">
            {value.product === "MIS"
              ? "Leveraged, and squared off in the afternoon whether you ask or not."
              : "The shares are yours overnight."}
          </span>
        </div>
        <div className="field">
          <label htmlFor="perTrade">Per trade (₹)</label>
          <input
            id="perTrade"
            type="number"
            min={100}
            step={500}
            value={value.perTradeRupees}
            onChange={(e) => set("perTradeRupees", Number(e.target.value))}
          />
          <span className="hint">The quantity is worked out from this and the price.</span>
        </div>
      </div>

      <div className="grid cols-3">
        <div className="field">
          <label htmlFor="maxPositions">Most positions at once</label>
          <input
            id="maxPositions"
            type="number"
            min={1}
            max={50}
            value={value.maxPositions}
            onChange={(e) => set("maxPositions", Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="maxTrades">Most trades a day</label>
          <input
            id="maxTrades"
            type="number"
            min={1}
            max={200}
            value={value.maxTradesPerDay}
            onChange={(e) => set("maxTradesPerDay", Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="dayLoss">Stop for the day at (₹)</label>
          <input
            id="dayLoss"
            type="number"
            min={100}
            step={500}
            value={value.dailyLossRupees}
            onChange={(e) => set("dailyLossRupees", Number(e.target.value))}
          />
          <span className="hint">
            Realised losses. The strategy halts itself and waits for you.
          </span>
        </div>
      </div>

      <div className="grid cols-3">
        <div className="field">
          <label htmlFor="stop">Stop loss (%)</label>
          <input
            id="stop"
            type="number"
            min={0.1}
            max={99}
            step={0.1}
            value={value.stopLossPct}
            onChange={(e) => set("stopLossPct", e.target.value)}
            placeholder="none"
          />
        </div>
        <div className="field">
          <label htmlFor="target">Target (%)</label>
          <input
            id="target"
            type="number"
            min={0.1}
            step={0.1}
            value={value.targetPct}
            onChange={(e) => set("targetPct", e.target.value)}
            placeholder="none"
          />
        </div>
        <div className="field">
          <label htmlFor="trail">Trailing stop (%)</label>
          <input
            id="trail"
            type="number"
            min={0.1}
            max={99}
            step={0.1}
            value={value.trailingPct}
            onChange={(e) => set("trailingPct", e.target.value)}
            placeholder="none"
          />
          <span className="hint">Follows the best price since entry. The fixed stop is the floor.</span>
        </div>
      </div>

      <div className="grid cols-3">
        <div className="field">
          <label htmlFor="from">Entries from</label>
          <input
            id="from"
            type="time"
            value={value.entryFrom}
            onChange={(e) => set("entryFrom", e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="to">Entries until</label>
          <input
            id="to"
            type="time"
            value={value.entryTo}
            onChange={(e) => set("entryTo", e.target.value)}
          />
          <span className="hint">Nothing new is opened outside this window.</span>
        </div>
        <div className="field">
          <label htmlFor="squareOff">Square off at</label>
          <input
            id="squareOff"
            type="time"
            value={value.squareOffAt}
            onChange={(e) => set("squareOffAt", e.target.value)}
          />
          <span className="hint">
            {value.product === "MIS"
              ? "Required for intraday — 15:10 is used if left empty."
              : "Optional for delivery."}
          </span>
        </div>
      </div>

      <CostPanel
        notional={Number(value.perTradeRupees) || 0}
        product={value.product}
        tradesPerDay={Number(value.maxTradesPerDay) || 0}
      />
    </div>
  );
}

/**
 * What this strategy will pay Zerodha, before it has been right about anything.
 *
 * Shown here, beside the numbers that determine it, rather than in a help page
 * — because this is the moment somebody types "10 trades a day" without
 * realising what that costs. On a small account the charges are not a rounding
 * error against the edge; frequently they are larger than it.
 */
function CostPanel({
  notional,
  product,
  tradesPerDay,
}: {
  notional: number;
  product: Product;
  tradesPerDay: number;
}) {
  if (notional <= 0) return null;

  const cost = roundTripCost(notional, product);
  const daily = cost.rupees * tradesPerDay;

  // Above roughly half a per cent a round trip, the rule has to be right by
  // more than most rules are right by. Worth saying so plainly.
  const heavy = cost.pct >= 0.5;

  return (
    <div className={`banner ${heavy ? "warn" : "info"}`} style={{ marginBottom: 0 }}>
      <div className="body">
        <strong>
          About {rupees(cost.rupees, 0)} a round trip — {cost.pct.toFixed(2)}% of{" "}
          {rupees(notional)}
        </strong>
        <p>
          {heavy ? (
            <>
              The rule has to be right by more than {cost.pct.toFixed(2)}% every trade
              just to break even. At {tradesPerDay} trade{tradesPerDay === 1 ? "" : "s"} a
              day that is {rupees(daily, 0)} of costs daily.{" "}
              {product === "CNC"
                ? "Most of it is the flat depository charge on each delivery sell, which does not shrink with the trade — larger, rarer positions pay it less often."
                : "Fewer, longer trades pay this less often."}
            </>
          ) : (
            <>
              At {tradesPerDay} trade{tradesPerDay === 1 ? "" : "s"} a day, about{" "}
              {rupees(daily, 0)} of costs daily. Estimated — the exact figures are on
              Zerodha&apos;s contract note.
            </>
          )}
        </p>
      </div>
    </div>
  );
}
